import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { ExampleQuestionsCarousel } from "components/shared/ExampleQuestionsCarousel/ExampleQuestionsCarousel";
import { QuestionFilterPanel, EMPTY_QUESTION_FILTER } from "./QuestionFilterPanel";
import { QuestionFilterView } from "./QuestionFilterView";
import { useLessonQuestions } from "hooks/useCurriculum/useCurriculum";
import { buildingTowardOrTier1, leafStandardCode, sortPrerequisites, sortStandardsByCode } from "utils/standards";
import { PrerequisiteTierSections } from "components/shared/PrerequisiteTierSections";
// Shared tab styling — keeps the lesson detail tabs pixel-identical to the
// already-shipped standard-assign flow (StandardDetailView).
import "components/pages/GroupCodeStandardSearch/groupCodeStandardSearch.css";
import pie_chart from "assets/pie-chart.svg";
import { useHostPageName, usePanelFirstView, useViewedPageTracking } from "hooks/usePageViewTracker";

// ---- Styled building blocks ----------------------------------------------
const DetailBreadcrumb = styled.div`
  font-size: 14px;
  color: #6b6967;
  margin-bottom: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const InfoIcon = styled.img`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
`;

const InfoEyebrow = styled.div`
  color: #ba4a21;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const InfoTitle = styled.h2`
  margin: 0;
  color: #002e19;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
`;

const InfoDescription = styled.p`
  margin: 4px 0 0;
  color: #6b6967;
  font-size: 14px;
  line-height: 22px;
`;

// Header info sections above the tabs — Learning Goals + Lesson Narrative.
const InfoSection = styled.div`
  margin: 16px 0 0;

  & + & {
    margin-top: 24px;
  }
`;

const SectionHeading = styled.h3`
  margin: 0 0 4px;
  color: #002e19;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.25px;
  line-height: 26px;
`;

const NarrativeText = styled.p`
  margin: 0;
  color: #002e19;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;

  ${({ $clamped }) => $clamped && `
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  `}
`;

const ShowMoreButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 6px;
  color: #524fc9;
  cursor: pointer;
  display: inline-flex;
  /* Buttons don't inherit the surrounding type by default. */
  font-family: inherit;
  font-size: 18px;
  height: 48px;
  letter-spacing: 0.25px;
  line-height: 26px;
  margin-top: 4px;
  padding: 0 8px 0 0;
`;

const ShowMoreLabel = styled.span`
  padding: 0 4px;
`;

const Chevron = styled.svg`
  flex-shrink: 0;
  transform: ${({ $flipped }) => ($flipped ? "rotate(180deg)" : "none")};
  transition: transform 0.15s ease;
`;

const GoalsList = styled.ul`
  margin: 0;
  padding-left: 24px;
  list-style: disc;
  color: #002e19;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
`;

// Tab bar styled locally (not via the shared .detail-tab CSS) so the lesson
// flow can match the Figma without altering the standard flow's tabs.
const TabBar = styled.div`
  display: flex;
  align-items: flex-start;
  border-bottom: 1px solid #acafad;
  margin: 8px 0 16px;
`;

const Tab = styled.button`
  background: none;
  border: none;
  border-bottom: 3px solid ${(p) => (p.$active ? "#605DEC" : "transparent")};
  margin-bottom: -1px;
  padding: 24px 16px;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${(p) => (p.$active ? "#524FC9" : "#002E19")};
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s;
  &:disabled {
    color: #c4c2be;
    cursor: not-allowed;
  }
  &:hover:not(:disabled):not([aria-selected="true"]) {
    color: #605dec;
  }
`;


const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$v3 ? "16px" : "4px")};
  padding: ${(p) => (p.$v3 ? "0 0 24px" : "24px")};
  border-bottom: ${(p) => (p.$last ? "none" : p.$v3 ? "1px solid #ACAFAD" : "1px solid #D5D3CE")};
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardCode = styled.div`
  color: #ba4a21;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 18px;
  text-transform: uppercase;
`;

const CardTitle = styled.div`
  color: #002e19;
  font-size: ${(p) => (p.$v3 ? "20px" : "16px")};
  font-weight: ${(p) => (p.$v3 ? "600" : "700")};
  letter-spacing: ${(p) => (p.$v3 ? "0" : "0.25px")};
  line-height: ${(p) => (p.$v3 ? "28px" : "22px")};
  ${(p) => p.$v3 && `font-family: "ABC Marfa", sans-serif;`}
`;

const CardDescription = styled.div`
  color: #002e19;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.25px;
  line-height: 22px;
`;

const ViewQuestionsButton = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: #002e19;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  padding: 4px 0;
  & svg.chevron {
    transition: transform 0.15s;
    transform: ${(p) => (p.$expanded ? "rotate(90deg)" : "none")};
  }
`;

const EmptyState = styled.p`
  color: #6b6967;
  font-size: 16px;
  padding: 16px 0;
`;

const LoadingBlock = styled.div`
  text-align: center;
  padding: 32px;
`;

// ---- Advanced Selection (question filter) building blocks -----------------
// Title + subtitle sit in a left column with the Filter Questions button on the
// right of the same row (per Figma 4253-20038).
const BankHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin: 24px 0 8px;
`;

const BankHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const BankTitle = styled.h3`
  margin: 0;
  color: #002e19;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
`;

const BankSubtitle = styled.p`
  margin: 0;
  color: #002e19;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
`;

const FilterQuestionsButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #605dec;
  border-radius: 8px;
  color: #524fc9;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  padding: 12px 20px;
  white-space: nowrap;
  &:hover { background: #eaeafc; }
  &:focus-visible { outline: 2px solid #605dec; outline-offset: 1px; }
`;

// "Lesson Focus" — the lesson's core standards above the question bank, for
// curricula that publish no learning goals.
const FocusSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
`;

const FocusCard = styled.div`
  padding: 0 24px 24px;
  border-bottom: 1px solid #acafad;
`;

const FocusTitle = styled.div`
  color: #002e19;
  font-family: "ABC Marfa", sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
`;

const FocusNote = styled.p`
  margin: 0;
  color: #6b6967;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 22px;
  max-width: 60ch;
  padding: 0 24px;
`;

// A single related-standard card with an inline "View Questions" accordion.
// When `enableFilter`, the card also carries a compact per-standard filter (Learning
// Component + Question Type) whose selection is lifted via getFilter/setFilter keyed by
// (trackType, item.code) — the prereq/extension equivalent of the Core filter panel.
const RelatedStandardCard = ({
  item,
  last,
  id,
  enableFilter = false,
  trackType,
  lessonId,
  getFilter,
  setFilter,
  v3 = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const filter = enableFilter && getFilter ? getFilter(trackType, item.code) : null;

  // Plain (non-filter) mode: the standard's lesson-scoped questions, fetched on
  // expand and paginated (total + loadMore) like every other carousel. Filter mode
  // delegates fetching + rendering to the shared QuestionFilterView.
  const plain = useLessonQuestions(
    !enableFilter && expanded ? lessonId : null,
    { standardCode: item.code },
  );

  return (
    <Card $last={last} $v3={v3} id={id}>
      <CardInfo>
        <CardCode>STANDARD - {item.code}</CardCode>
        {item.name && <CardTitle $v3={v3}>{item.name}</CardTitle>}
        {item.description && <CardDescription>{item.description}</CardDescription>}
      </CardInfo>
      <ViewQuestionsButton $expanded={expanded} onClick={() => setExpanded((e) => !e)}>
        <svg className="chevron" width="10" height="10" viewBox="0 0 10 10">
          <path d="M3 1L7 5L3 9" stroke="#002E19" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        View Questions
      </ViewQuestionsButton>
      {expanded &&
        (enableFilter && filter ? (
          <QuestionFilterView
            lessonId={lessonId}
            standardCode={item.code}
            filter={filter}
            onFilterChange={(next) => setFilter && setFilter(trackType, item.code, next)}
            showStandards={false}
            showAutoAssign
          />
        ) : plain.loading ? (
          <LoadingBlock>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </LoadingBlock>
        ) : plain.questions.length > 0 ? (
          <ExampleQuestionsCarousel questions={plain.questions} total={plain.total} onLoadMore={plain.loadMore} />
        ) : (
          <EmptyState>No questions available for this standard.</EmptyState>
        ))}
    </Card>
  );
};

const PREREQ_SUBTITLE = "The adjacent standards that directly support learning the Core Standard.";
const EXTENSION_SUBTITLE = "The standards that build directly upon the Core Standard.";

// Map the lesson's flat alignments into the card shape used by the tabs. Carries
// learning_components through as learningComponents, matching the Core tab's
// naming so RelatedStandardCard's `item.learningComponents` read works the same
// way on every tab.
const toStandardItems = (alignments) =>
  (alignments || [])
    .map((a) => ({
      code: a.standard_code,
      name: a.standard_name,
      description: a.standard_description,
      learningComponents: a.learning_components,
    }))
    .filter((s) => s.code);


const LESSON_PANEL_NAME = "Lesson Detail";
const LESSON_TAB_LABELS = { core: "Core", building_on: "Prerequisites", building_toward: "Extensions" };
const firstAvailableTab = (available) => Object.keys(LESSON_TAB_LABELS).find((key) => available[key]);

export const LessonDetailPanel = ({
  lesson,
  module = null,
  unitOrdinal = null,
  questions,
  questionsLoading,
  questionsTotal,
  onLoadMoreQuestions,
  onAssign,
  onBack,
  showAssignButton = true,
  // Advanced Selection (question filter) — off unless the Assign Lesson flow opts in.
  enableQuestionFilter = false,
  filterOpen = false,
  onToggleFilter,
  // Heading and blurb over the core question bank. Both default to the v2 copy, so a
  // caller that doesn't care about the experience variant reads exactly as it did before.
  bankTitle = "Core Lesson Question Bank",
  bankSubtitle = "Let OKO automatically assign questions from the bank below, or filter the set to customize which questions are included.",
  // v3 hides the auto-assign checkbox.
  showAutoAssign = true,
  // "v3" renders the Prerequisite/Extension standard cards in the Figma style.
  standardsSectionVariant = "v2",
  // (trackType, standardCode) => QuestionFilterState ; (trackType, standardCode, next) => void
  getFilter,
  setFilter,
  coreFacets,
  // Fail-open like QuestionFilterPanel's own defaults: a parent that doesn't pass these
  // renders exactly as it did before rather than sitting in a permanent "no data" state.
  coreFacetsLoaded = true,
  coreFacetsError = false,
}) => {
  const coreFilter = (getFilter && getFilter("core", null)) || EMPTY_QUESTION_FILTER;

  const addressing = useMemo(() => lesson?.addressing || [], [lesson]);
  // The facets response only carries standard codes; look up each one's name from
  // the lesson's own addressing set for the Standards dropdown's "CODE: Name" labels.
  // Keyed by the leaf spelling the facets return.
  const coreStandardNames = useMemo(
    () =>
      Object.fromEntries(
        addressing.filter((a) => a.standard_code).map((a) => [leafStandardCode(a.standard_code), a.standard_name]),
      ),
    [addressing],
  );

  // Flat prereq / extension lists come from the lesson's own alignments. When
  // the curriculum publishes none, the server falls back to graph-derived
  // tiers — and its assign flow accepts and targets the same tier codes, so
  // the detail view and the Assign Groups modal stay in agreement either way.
  const prereqItems = useMemo(() => sortPrerequisites(toStandardItems(lesson?.building_on)), [lesson]);
  // Tiered prerequisites ({level, standards[]} groups, same shape as the standard
  // relationships endpoint). When present they replace the flat building_on list —
  // a lesson with zero building_on rows can still carry server-derived tiers, so
  // neither list gates the other.
  const prereqTiers = useMemo(
    () => (lesson?.prerequisite_tiers || []).filter((g) => g.standards?.length),
    [lesson],
  );
  const extItems = useMemo(() => sortStandardsByCode(toStandardItems(buildingTowardOrTier1(lesson))), [lesson]);
  const standardsByTab = { building_on: prereqItems, building_toward: extItems };

  const goalItems = useMemo(
    () =>
      (lesson?.learning_goals || "")
        .split("\n")
        .map((g) => g.trim())
        .filter(Boolean),
    [lesson?.learning_goals],
  );
  // Curricula without learning goals show the core standards instead.
  const focusItems = useMemo(
    () => (goalItems.length > 0 ? [] : sortStandardsByCode(toStandardItems(addressing))),
    [goalItems, addressing],
  );
  const moduleCodes = useMemo(
    () => (module?.standards || []).map((s) => (typeof s === "string" ? s : s.code)).filter(Boolean),
    [module],
  );
  const lessonCodes = useMemo(() => focusItems.map((item) => item.code), [focusItems]);
  const showModuleStandardsNote = useMemo(() => {
    if (!module || !moduleCodes.length || !lessonCodes.length) return false;
    if (moduleCodes.length !== lessonCodes.length) return false;
    const lessonSet = new Set(lessonCodes);
    return moduleCodes.every((code) => lessonSet.has(code));
  }, [module, moduleCodes, lessonCodes]);

  const breadcrumbText = useMemo(() => {
    if (!module) return null;
    const unitText = unitOrdinal || lesson?.unit_ordinal_name;
    return [unitText, module.ordinal_name].filter(Boolean).join(" › ");
  }, [module, unitOrdinal, lesson?.unit_ordinal_name]);

  const [activeTab, setActiveTab] = useState("core");
  // Reset to the Core tab when the lesson changes.
  useEffect(() => {
    setActiveTab("core");
  }, [lesson?.id]);

  // A tab is available only when it has content: Core needs a core standard or
  // example questions; prereq/extension need their alignments.
  const hasCore = addressing.length > 0 || (questions?.length || 0) > 0 || questionsLoading;
  const hasBuildingOn = prereqTiers.length > 0 || prereqItems.length > 0;
  const hasBuildingToward = extItems.length > 0;

  // If the active tab loses its content (e.g. switching lessons or core codes),
  // fall back to the first tab that still has content.
  useEffect(() => {
    const available = { core: hasCore, building_on: hasBuildingOn, building_toward: hasBuildingToward };
    if (!available[activeTab]) {
      const next = firstAvailableTab(available);
      if (next) setActiveTab(next);
    }
  }, [activeTab, hasCore, hasBuildingOn, hasBuildingToward]);

  // One view per lesson shown, naming the tab it opens on (the fallback above
  // has picked it by then when Core is empty).
  const hostPageName = useHostPageName();
  const { trackView } = useViewedPageTracking();
  const trackTabView = (key) =>
    trackView(hostPageName, { panel: LESSON_PANEL_NAME, lesson_id: lesson.id, tab: LESSON_TAB_LABELS[key] });
  usePanelFirstView(lesson?.id, () =>
    trackTabView(firstAvailableTab({ core: hasCore, building_on: hasBuildingOn, building_toward: hasBuildingToward }) || "core"),
  );

  // The narrative collapses to two lines; the toggle only appears when there is
  // more text than that, so short narratives don't get a no-op control.
  const narrativeRef = useRef(null);
  const [narrativeExpanded, setNarrativeExpanded] = useState(false);
  const [narrativeOverflows, setNarrativeOverflows] = useState(false);

  useEffect(() => {
    setNarrativeExpanded(false);
  }, [lesson?.id]);

  // Measured only while collapsed — expanded, the element is its full height and
  // would always compare equal.
  // ponytail: no resize listener; the panel doesn't reflow. Add one if it does.
  useLayoutEffect(() => {
    if (narrativeExpanded) return;
    const el = narrativeRef.current;
    setNarrativeOverflows(!!el && el.scrollHeight > el.clientHeight);
  }, [lesson?.lesson_narrative, narrativeExpanded]);

  if (!lesson) return null;

  const eyebrow = [lesson.course_name, lesson.unit_ordinal_name, lesson.ordinal_name]
    .filter(Boolean)
    .join(", ")
    .toUpperCase();
  const description = lesson.student_facing_goal || null;
  const narrative = lesson.lesson_narrative || null;

  const renderTab = (key, label, enabled, tooltip) => (
    <span className="detail-tab-wrapper" key={key}>
      <Tab
        role="tab"
        aria-selected={activeTab === key}
        $active={activeTab === key}
        onClick={() => {
          if (!enabled) return;
          if (key !== activeTab) trackTabView(key);
          setActiveTab(key);
        }}
        disabled={!enabled}
      >
        {label}
      </Tab>
      {!enabled && tooltip && (
        <span className="detail-tab-tooltip" role="tooltip">
          {tooltip}
          <svg className="detail-tab-tooltip-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none">
            <path d="M0 0V12L16 0H0Z" fill="#EAEAFC" />
          </svg>
        </span>
      )}
    </span>
  );

  // Prereq/Extension tab → assign contract track_type. (The lesson flow accepts
  // "extensions"; the backend also treats "extension" as equivalent.)
  const TAB_TRACK_TYPE = { building_on: "prerequisites", building_toward: "extensions" };

  // A tab body: the lesson's related-standard cards for this tab. Standards are
  // the lesson's own alignments, so questions are fetched lesson-scoped.
  const renderRelationTab = (tabKey, header, subtitle) => {
    const items = standardsByTab[tabKey];
    return (
      <>
          <div className="level-group" style={{ background: "transparent" }}>
            <div className="relationships-header">
              <div className="relationships-header-title">{header}</div>
              <div className="relationships-header-subtitle">{subtitle}</div>
            </div>
            <div className="level-group-cards" style={{ background: "transparent", border: "none", borderRadius: 0 }}>
              {items.map((s, i) => (
                <RelatedStandardCard
                  key={s.code || i}
                  item={s}
                  last={i === items.length - 1}
                  enableFilter={enableQuestionFilter}
                  trackType={TAB_TRACK_TYPE[tabKey]}
                  lessonId={lesson.id}
                  getFilter={getFilter}
                  setFilter={setFilter}
                  v3={standardsSectionVariant === "v3"}
                />
              ))}
            </div>
          </div>
      </>
    );
  };

  return (
    <>
      <div className="ls-detail-panel">
        {breadcrumbText && (
          <DetailBreadcrumb className="crumb ls-detail-breadcrumb">
            {breadcrumbText}
          </DetailBreadcrumb>
        )}
        <InfoRow>
          <InfoIcon src={pie_chart} alt="" />
          <div>
            {eyebrow && <InfoEyebrow>{eyebrow}</InfoEyebrow>}
            <InfoTitle>{lesson.name}</InfoTitle>
            {description && <InfoDescription>{description}</InfoDescription>}
          </div>
        </InfoRow>

        {goalItems.length > 0 && (
          <InfoSection>
            <SectionHeading>Learning Goals</SectionHeading>
            <GoalsList>
              {goalItems.map((goal, i) => (
                <li key={i}>{goal}</li>
              ))}
            </GoalsList>
          </InfoSection>
        )}

        {narrative && (
          <InfoSection>
            <SectionHeading>Lesson Narrative</SectionHeading>
            <NarrativeText id="lesson-narrative" ref={narrativeRef} $clamped={!narrativeExpanded}>
              {narrative}
            </NarrativeText>
            {narrativeOverflows && (
              <ShowMoreButton
                type="button"
                aria-controls="lesson-narrative"
                aria-expanded={narrativeExpanded}
                onClick={() => setNarrativeExpanded((v) => !v)}
              >
                <ShowMoreLabel>{narrativeExpanded ? "Show Less" : "Show More"}</ShowMoreLabel>
                <Chevron
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  $flipped={narrativeExpanded}
                >
                  <path d="M6 9L12 15L18 9" />
                </Chevron>
              </ShowMoreButton>
            )}
          </InfoSection>
        )}

        <TabBar className="ls-lesson-tabs" role="tablist">
          {renderTab("core", LESSON_TAB_LABELS.core, hasCore, "No standards are mapped to this lesson.")}
          {renderTab(
            "building_on",
            LESSON_TAB_LABELS.building_on,
            hasBuildingOn,
            "No prerequisites available for this lesson.",
          )}
          {renderTab(
            "building_toward",
            LESSON_TAB_LABELS.building_toward,
            hasBuildingToward,
            "No extensions available for this lesson.",
          )}
        </TabBar>

        {activeTab === "core" && (
          <div className="tab-panel">
            {focusItems.length > 0 && (
              <FocusSection aria-labelledby="lesson-focus-heading">
                <BankHeaderText>
                  <BankTitle id="lesson-focus-heading">Lesson Focus</BankTitle>
                  <BankSubtitle>This lesson includes the following standards:</BankSubtitle>
                </BankHeaderText>
                {focusItems.map((item) => (
                  <FocusCard key={item.code}>
                    <CardInfo>
                      <CardCode>STANDARD - {item.code}</CardCode>
                      {item.name && <FocusTitle>{item.name}</FocusTitle>}
                      {item.description && <CardDescription>{item.description}</CardDescription>}
                    </CardInfo>
                  </FocusCard>
                ))}
                {showModuleStandardsNote && (
                  <FocusNote>
                    These standards are set for all of {module.ordinal_name || "the module"}. Every lesson in the module shows the same list.
                  </FocusNote>
                )}
              </FocusSection>
            )}

            {enableQuestionFilter && (
              <>
                <BankHeaderRow>
                  <BankHeaderText>
                    <BankTitle>{bankTitle}</BankTitle>
                    <BankSubtitle>{bankSubtitle}</BankSubtitle>
                  </BankHeaderText>
                  {!filterOpen && (
                    <FilterQuestionsButton
                      type="button"
                      onClick={() => onToggleFilter && onToggleFilter()}
                    >
                      Filter Questions
                    </FilterQuestionsButton>
                  )}
                </BankHeaderRow>
                {filterOpen && (
                  <QuestionFilterPanel
                    filter={coreFilter}
                    onChange={(next) => setFilter && setFilter("core", null, next)}
                    facets={coreFacets}
                    hasLoaded={coreFacetsLoaded}
                    error={coreFacetsError}
                    showStandards
                    showAutoAssign={showAutoAssign}
                    standardNames={coreStandardNames}
                  />
                )}
              </>
            )}

            {questionsLoading ? (
              <ExampleQuestionsCarousel loading />
            ) : questions?.length > 0 ? (
              <ExampleQuestionsCarousel questions={questions} total={questionsTotal} onLoadMore={onLoadMoreQuestions} />
            ) : (
              <EmptyState>No example problems available for this lesson yet.</EmptyState>
            )}
          </div>
        )}

        {activeTab === "building_on" && (
          <div className="tab-panel">
            {prereqTiers.length > 0 ? (
              <PrerequisiteTierSections
                groups={prereqTiers}
                renderCard={(s, i, last) => (
                  <RelatedStandardCard
                    key={s.code || i}
                    item={{ ...s, learningComponents: s.learning_components }}
                    last={last}
                    enableFilter={enableQuestionFilter}
                    trackType="prerequisites"
                    lessonId={lesson.id}
                    getFilter={getFilter}
                    setFilter={setFilter}
                    v3={standardsSectionVariant === "v3"}
                  />
                )}
              />
            ) : (
              renderRelationTab("building_on", "Prerequisites", PREREQ_SUBTITLE)
            )}
          </div>
        )}

        {activeTab === "building_toward" && (
          <div className="tab-panel">
            {renderRelationTab("building_toward", "Extensions", EXTENSION_SUBTITLE)}
          </div>
        )}
      </div>

      {showAssignButton && (
        <div className="ls-assign-bar">
          <button className="ls-save-secondary" onClick={onBack}>
            Back to Groups
          </button>
          <button className="ls-save-primary" onClick={() => onAssign(lesson.id)}>
            Create Lesson Groups
          </button>
        </div>
      )}
    </>
  );
};
